<template>
  <Dialog v-model="visible" :title="$t('monopoly.rewardRecord')">
    <div class="reward-record">
      <div class="list-header">
        <div class="col reward">{{ $t('monopoly.rewardRecordDialog.reward') }}</div>
        <div class="col time">{{ $t('monopoly.rewardRecordDialog.time') }}</div>
        <div class="col status">{{ $t('monopoly.rewardRecordDialog.status') }}</div>
      </div>
      <div class="record-list" v-if="state.list.length">
        <div
          class="record-item"
          v-for="(item, index) in state.list"
          :key="index"
        >
          <div class="col reward">
            <template v-if="parseProps(item.propSnapshot).length">
              <div
                class="prop-line"
                v-for="(prop, pIdx) in parseProps(item.propSnapshot)"
                :key="pIdx"
              >
                {{ formatPropText(prop) }}
              </div>
            </template>
            <span v-else>—</span>
          </div>
          <div class="col time">{{ formatTime(item.createTime) }}</div>
          <div class="col status">{{ statusText(item) }}</div>
        </div>
      </div>
      <div class="empty-tip" v-else-if="!state.loading">
        {{ $t('monopoly.rewardRecordDialog.empty') }}
      </div>
    </div>
  </Dialog>
</template>

<script setup>
import { computed, reactive, watch, defineProps, defineEmits } from 'vue'
import { useI18n } from 'vue-i18n'
import dayjs from 'dayjs'
import Dialog from '../Dialog/index.vue'
import MonopolyApi from '@/views/act/monopoly/api'
import { formatPropText as formatPropTextUtil } from '@/views/act/monopoly/utils/prop'

const EXTRACT_PENDING = 0
// const EXTRACT_DONE = 1
const EXTRACT_DOING = 2

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:modelValue'])
const { t } = useI18n()

const visible = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val),
})

const state = reactive({
  loading: false,
  list: [],
})

const statusText = (item) => {
  // 棋盘投掷：未提取 / 已提取；其他来源已直接发放
  if (item.rewardSource === 'roll') {
    const status = Number(item.extractStatus)
    if (status === EXTRACT_PENDING || status === EXTRACT_DOING) {
      return t('monopoly.rewardRecordDialog.statusPending')
    }
    return t('monopoly.rewardRecordDialog.statusExtracted')
  }
  return t('monopoly.rewardRecordDialog.statusSent')
}

const parseProps = (raw) => {
  if (!raw) return []
  try {
    const list = typeof raw === 'string' ? JSON.parse(raw) : raw
    return Array.isArray(list) ? list : []
  } catch (e) {
    return []
  }
}

const formatPropText = (prop) => formatPropTextUtil(t, prop)

const formatTime = (ms) => {
  const value = Number(ms || 0)
  if (!value) return '—'
  return dayjs(value).format('YYYY/M/D HH:mm')
}

const loadRecords = async () => {
  state.loading = true
  try {
    const res = await MonopolyApi.getRewardRecord()
    // 兜底：前端也过滤掉 extract 行
    state.list = (res?.list || []).filter((item) => item.rewardSource !== 'extract')
  } catch (e) {
    console.error(e)
    state.list = []
  } finally {
    state.loading = false
  }
}

watch(
  () => props.modelValue,
  (val) => {
    if (val) {
      loadRecords()
    } else {
      state.list = []
    }
  }
)
</script>

<style lang="less" scoped>
.reward-record {
  width: 100%;
  box-sizing: border-box;
  color: #12215c;
}

.list-header,
.record-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.list-header {
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 8px;
  padding: 12px 8px;
  background: rgba(159, 247, 253, 0.55);
  border-radius: 8px;
  text-align: center;
}

.record-list {
  max-height: 480px;
  overflow-y: auto;
}

.record-item {
  font-size: 22px;
  line-height: 1.4;
  padding: 16px 8px;
  text-align: center;
  border-bottom: 1px solid rgba(18, 33, 92, 0.08);

  &:last-child {
    border-bottom: none;
  }
}

.col {
}

.col.reward {
  flex: 1;
  min-width: 0;
}

.col.time {
  width: 180px;
  flex-shrink: 0;
}

.col.status {
  width: 120px;
  flex-shrink: 0;
}

.prop-line {
  margin-bottom: 4px;

  &:last-child {
    margin-bottom: 0;
  }
}

.empty-tip {
  padding: 48px 0;
  text-align: center;
  font-size: 24px;
  color: rgba(18, 33, 92, 0.6);
}
</style>
